'use client';

import React, { useState } from "react";

interface FilterOption {
  name: string;
  count: number;
}

interface FiltersProps {
  q: string;
  setQ: (q: string) => void;
  category: string;
  setCategory: (cat: string) => void;
  categories: FilterOption[];
  tags: string[];
  toggleTag: (tag: string) => void;
  allTags: FilterOption[];
  featured: boolean;
  setFeatured: (f: boolean) => void;
  clearFilters: () => void;
}

export default function Filters({
  q, setQ,
  category, setCategory, categories,
  tags, toggleTag, allTags,
  featured, setFeatured,
  clearFilters,
}: FiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const INITIAL_VISIBLE_TAGS = 8;

  return (
    <div className="w-full max-w-5xl mx-auto mb-10 flex flex-col gap-8 min-w-0 px-1">
      
      {/* 1. Search Bar & Category Dropdown */}
      <div className="flex flex-col sm:flex-row gap-4">
        <input
          type="text"
          placeholder="Search transmissions..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 px-5 py-3 border rounded-xl bg-white/50 dark:bg-zinc-900/50 backdrop-blur-sm border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all shadow-sm"
        />

        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="px-4 py-3 border rounded-xl bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all cursor-pointer shadow-sm"
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat.name} value={cat.name}>
              {cat.name} ({cat.count})
            </option>
          ))}
        </select>
      </div>

      {/* 2. Hybrid Tags Section */}
      <div className="space-y-4 w-full min-w-0 flex flex-col">
        <div className="flex items-center justify-between px-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 dark:text-zinc-500">
            Signal Tags
          </span>

          {allTags.length > INITIAL_VISIBLE_TAGS && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="hidden sm:block text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline transition-all"
            >
              {isExpanded ? "Collapse" : `Expand All (${allTags.length})`}
            </button>
          )}
        </div>

        {/* Container Fix: 'relative' and 'overflow-hidden' on this parent 
            prevents the negative margin of the scroll area from stretching the page.
        */}
        <div className="relative w-full overflow-hidden">
          <div className="flex overflow-x-auto pb-4 sm:pb-0 sm:flex-wrap gap-2 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0 touch-pan-x outline-none">
            {allTags.map((tag, index) => {
              const isActive = tags.includes(tag.name);
              // Only hide extra tags on desktop if not expanded. On mobile, we let them scroll.
              const isHiddenOnDesktop = !isExpanded && index >= INITIAL_VISIBLE_TAGS;

              return (
                <button
                  key={tag.name}
                  type="button"
                  onClick={() => toggleTag(tag.name)}
                  className={`
                    flex-shrink-0 flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-bold transition-all
                    ${isHiddenOnDesktop ? "sm:hidden" : "flex"}
                    ${isActive
                      ? "bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-600/20 scale-105"
                      : "bg-white dark:bg-zinc-900 text-zinc-500 dark:text-zinc-400 border-zinc-200 dark:border-zinc-800 hover:border-indigo-400"
                    }
                  `}
                >
                  <span className="truncate max-w-[8rem]">{tag.name}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                    isActive ? "bg-white/20 text-white" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"
                  }`}>
                    {tag.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 3. Footer: Featured Toggle & Reset */}
      <div className="flex flex-col sm:flex-row items-center justify-between pt-6 border-t border-zinc-200 dark:border-zinc-800 gap-4">
        <label className="flex items-center gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={featured}
            onChange={(e) => setFeatured(e.target.checked)}
            className="w-5 h-5 rounded-md border-zinc-300 dark:border-zinc-700 text-indigo-600 focus:ring-indigo-500 bg-transparent transition-all"
          />
          <span className="text-xs font-bold uppercase tracking-widest text-zinc-600 dark:text-zinc-400 group-hover:text-indigo-500 transition-colors">
            Featured Signals Only
          </span>
        </label>

        {(q || category || tags.length > 0 || featured) && (
          <button
            onClick={clearFilters}
            className="text-rose-500 hover:text-rose-600 font-bold text-[10px] uppercase tracking-widest transition-colors flex items-center gap-2 px-3 py-1 rounded-lg hover:bg-rose-500/10"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Reset All Filters
          </button>
        )}
      </div>
    </div>
  );
}